/**
 * KEV dashboard — the reporting deadlines, the vulnerabilities and the customers.
 *
 * Three views, chosen in the header and remembered per viewer:
 *   - „Fristen“ (default, DEV-137): every vulnerability case on one time axis,
 *     see pages/meldefristen. Needs triage.read; `?view=fristen&case=` links here.
 *   - „CVE“ (DEV-133): CVE → affected products with their case → versions and
 *     customers, in sections by where the case stands. Needs triage.read.
 *   - „Kunde“: one grouped table per work section — Zu bewerten, Zu
 *     benachrichtigen, Benachrichtigt (plus „Ohne Handlungsbedarf“ when there
 *     is any). A customer expands into its ProduktInstanzen of that section, an
 *     instance into its components.
 *
 * Both overviews share the KPI strip; „Fällige Meldefristen“ opens the
 * deadlines filtered to the due cases. Customer notifications are created by
 * a confirmed product assessment and sent from the Benachrichtigungen page.
 *
 * What it keeps from the React port of app/web/dashboard.html, and why:
 *
 *   - ONE state for BOTH payloads. `GET /api/dashboard/overview` alone cannot
 *     tell an unassessed hit from one whose product verdict is still open: the
 *     assessment progress lives in `GET /api/dashboard/vulnerability-events`.
 *     See `notifyGate`.
 *   - `only_affected` is the ONLY filter that hits the server; free text and
 *     the sections are client-side over the loaded rows, so typing never
 *     triggers a CMDBuild round trip.
 *   - Automatic refresh (60 s) — see useDashboardData for the stale-closure and
 *     cleanup details.
 *
 * Permissions (frozen vocabulary):
 *   dashboard.read     — read this page (the API enforces it),
 *   triage.read        — the triage links and the lazy KEV/EPSS lookup.
 */

import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Banner from '../components/Banner';
import EmptyState from '../components/EmptyState';
import { KpiRow, type KpiItem } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import Spinner, { PageSpinner } from '../components/Spinner';
import { useAuth } from '../lib/auth';
import { fmtDate } from '../lib/format';
import type { DashboardData, OverviewRow, VulnerabilityEventRow } from '../lib/types';
import './dashboard/dashboard.css';
import CveView, { cveSectionId } from './dashboard/CveView';
import {
  groupRows,
  sectionGroups,
  workState,
  type WorkState,
} from './dashboard/dashboardGroups';
import { GroupTable } from './dashboard/GroupCells';
import {
  kpiCounters,
  matchesFilters,
  notifyGate,
  searchTokens,
  type DashboardCounter,
  type NotifyGate,
} from './dashboard/dashboardModel';
import { useDashboardData } from './dashboard/useDashboardData';
import { useExploitability, type ExploitabilityStore } from './dashboard/useExploitability';
import DeadlineTimeline from './meldefristen/DeadlineTimeline';
import type { TimelineFocus } from './meldefristen/timelineModel';

/** Shown under every failed load — the two things that are actually wrong. */
const CMDB_HINT = msg`Prüfen Sie die Verbindung zu CMDBuild und ob das Schema aktuell ist.`;

/** Stable empty defaults so the memos below do not see a new array every render. */
const EMPTY_ROWS: readonly OverviewRow[] = [];
const EMPTY_EVENTS: readonly VulnerabilityEventRow[] = [];

/** The work sections in the order of the workflow; translated at render. */
const SECTIONS: ReadonlyArray<{ state: WorkState; title: MessageDescriptor; empty: MessageDescriptor | null }> = [
  { state: 'assess', title: msg`Zu bewerten`, empty: msg`Keine offenen Bewertungen` },
  { state: 'notify', title: msg`Zu benachrichtigen`, empty: msg`Keine Benachrichtigung offen` },
  { state: 'notified', title: msg`Benachrichtigt`, empty: msg`Noch keine Benachrichtigung versendet` },
  // Shown only when it has entries.
  { state: 'none', title: msg`Ohne Handlungsbedarf`, empty: null },
];

const sectionId = (state: WorkState): string => `dash-section-${state}`;

type DashboardView = 'fristen' | 'cve' | 'kunde';

/** `label` names the switch button, `title` heads the page while the view is shown. */
const VIEWS: ReadonlyArray<{ view: DashboardView; label: MessageDescriptor; title: MessageDescriptor }> = [
  { view: 'fristen', label: msg`Fristen`, title: msg`Meldefristen` },
  { view: 'cve', label: msg`CVE`, title: msg`Schwachstellenübersicht` },
  { view: 'kunde', label: msg`Kunde`, title: msg`Kundenübersicht` },
];

/** Remembered per viewer; storage may be unavailable (private mode, previews). */
const MODE_STORAGE_KEY = 'dashboard.groupMode';

/** A remembered product view (until DEV-133) leads to the CVE view. */
function asView(value: string | null): DashboardView | null {
  if (value === 'produkt') return 'cve';
  return value === 'fristen' || value === 'cve' || value === 'kunde' ? value : null;
}

/** A deep link wins, then the remembered choice; the deadlines are the default. */
function initialView(params: URLSearchParams): DashboardView {
  const linked = asView(params.get('view'));
  if (linked) return linked;
  try {
    return asView(window.localStorage.getItem(MODE_STORAGE_KEY)) ?? 'fristen';
  } catch {
    return 'fristen';
  }
}

function writeView(view: DashboardView): void {
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, view);
  } catch {
    // Only a convenience — the page works without it.
  }
}

function linkedCase(params: URLSearchParams): number | null {
  const value = Number(params.get('case'));
  return Number.isInteger(value) && value > 0 ? value : null;
}

/** The view switch in the header; deadlines and CVEs need triage.read. */
function ViewSwitch({ current, withTriage, onChange }: Readonly<{
  current: DashboardView;
  withTriage: boolean;
  onChange: (view: DashboardView) => void;
}>): ReactNode {
  const { i18n } = useLingui();
  return (
    <fieldset className="seg view-switch">
      <legend className="visually-hidden"><Trans>Ansicht wählen</Trans></legend>
      {VIEWS.filter((item) => item.view === 'kunde' || withTriage).map((item) => (
        <button
          key={item.view}
          type="button"
          className={current === item.view ? 'on' : undefined}
          aria-pressed={current === item.view}
          onClick={() => onChange(item.view)}
        >
          {i18n._(item.label)}
        </button>
      ))}
    </fieldset>
  );
}

/** Where the KPI tiles lead. */
interface TileActions {
  openDeadlines: () => void;
  openTriage: () => void;
  notify: () => void;
  /** The section the notify tile scrolls to, if it does. */
  notifyControls?: string;
}

/** The KPI strip of both overviews; every action tile leads where the work is. */
function dashboardTiles(i18n: I18n, counters: readonly DashboardCounter[], actions: TileActions): KpiItem[] {
  return counters.map((counter): KpiItem => {
    const { value } = counter;
    switch (counter.key) {
      case 'deadlines':
        return { ...counter, ariaLabel: i18n._(msg`Fällige Meldefristen: ${value} in der Fristen-Ansicht öffnen`),
          onClick: actions.openDeadlines };
      case 'assessments':
        return { ...counter, ariaLabel: i18n._(msg`Offene Bewertungen: ${value} in der Triage öffnen`),
          onClick: actions.openTriage };
      case 'notify':
        return { ...counter, ariaLabel: i18n._(msg`Zu benachrichtigen: ${value} anzeigen`),
          controls: actions.notifyControls, onClick: actions.notify };
      default:
        return counter;
    }
  });
}

/** The due deadlines of the CVE payload; null without triage.read. */
function dueDeadlines(data: DashboardData): { due: number; overdue: number } | null {
  const summary = data.cves?.summary;
  return summary ? { due: summary.due_deadlines, overdue: summary.overdue_deadlines } : null;
}

/** The load error of both overviews, with the hint at what is actually wrong. */
function LoadError({ error }: Readonly<{ error: string | null }>): ReactNode {
  const { i18n } = useLingui();
  if (error === null) return null;
  return <Banner kind="error" sub={i18n._(CMDB_HINT)}><Trans>Übersicht konnte nicht geladen werden: {error}</Trans></Banner>;
}

function LoadState({ loading, error }: Readonly<{ loading: boolean; error: string | null }>): ReactNode {
  const { t } = useLingui();
  return (
    <>
      <LoadError error={error} />
      {loading ? <PageSpinner text={t`Übersicht wird geladen…`} /> : <EmptyState title={t`Keine Daten geladen`} />}
    </>
  );
}

function overviewEmptyText(rows: number, onlyAffected: boolean): MessageDescriptor {
  if (rows > 0) return msg`Nichts passt zur Suche.`;
  return onlyAffected
    ? msg`Keine Produktinstanzen mit KEV-Treffer. Deaktivieren Sie „Nur betroffene“, um alle anzuzeigen.`
    : msg`Keine Produktinstanzen vorhanden. Unter „Stammdaten“ anlegen oder per CSV importieren.`;
}

interface OverviewViewProps {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  onlyAffected: boolean;
  onOnlyAffectedChange: (onlyAffected: boolean) => void;
  canReadTriage: boolean;
  store: ExploitabilityStore;
  onOpenDeadlines: () => void;
}

/** The view „Kunde“: KPI row, filter and the work sections per customer. */
function OverviewView({
  data, loading, error, onlyAffected, onOnlyAffectedChange, canReadTriage, store, onOpenDeadlines,
}: Readonly<OverviewViewProps>): ReactNode {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const rows = data?.overview.rows ?? EMPTY_ROWS;
  const events = data?.events.events ?? EMPTY_EVENTS;

  // The gate is a cross-payload join; compute it once per payload, not per cell.
  const gates = useMemo(() => {
    const map = new Map<number, NotifyGate>();
    for (const row of rows) map.set(row.produkt.id, notifyGate(row, events));
    return map;
  }, [rows, events]);

  const tokens = useMemo(() => searchTokens(search), [search]);
  const visibleRows = useMemo(
    () => rows.filter((row) => matchesFilters(row, tokens)),
    [rows, tokens],
  );
  // Group titles fall back to a translated name and sort in the UI language.
  const sections = useMemo(() => {
    const groups = groupRows(visibleRows, gates);
    return SECTIONS.map((section) => {
      const own = sectionGroups(groups, section.state, gates);
      return { ...section, groups: own, instances: own.reduce((sum, group) => sum + group.rows.length, 0) };
    });
  }, [visibleRows, gates, locale]);

  // Unfiltered by the search: the tile describes the loaded payload. Open mails
  // per customer × case when the CVE payload is there (DEV-133).
  const toNotify = useMemo(
    () => data?.cves?.summary.open_mails
      ?? rows.filter((row) => workState(gates.get(row.produkt.id)) === 'notify').length,
    [data, rows, gates],
  );
  const kpiTiles = useMemo(
    (): KpiItem[] => (data === null ? [] : dashboardTiles(i18n,
      kpiCounters(data.overview.summary, data.events.offen, toNotify, dueDeadlines(data)), {
        openDeadlines: onOpenDeadlines,
        openTriage: () => navigate('/triage'),
        notifyControls: sectionId('notify'),
        notify: () =>
          document.getElementById(sectionId('notify'))?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      })),
    // `locale`: the counters are translated, re-build them on a language switch.
    [data, toNotify, navigate, onOpenDeadlines, i18n, locale],
  );

  const emptyText = i18n._(overviewEmptyText(rows.length, onlyAffected));

  if (data === null) return <LoadState loading={loading} error={error} />;

  return (
    <>
      <LoadError error={error} />

      <KpiRow counters={kpiTiles} label={t`Kennzahlen zur aktuellen Sicherheitslage`} />

      <div className="card dash-filter">
        <div className="table-toolbar">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder={t`Kunde, Produkt, Komponente oder CVE suchen…`}
            aria-label={t`Übersicht filtern`}
          />
          <div className="table-toolbar-options">
            <label className="check" title={t`Serverseitiger Filter: lädt die Übersicht neu`}>
              <input
                type="checkbox"
                checked={onlyAffected}
                onChange={(event) => onOnlyAffectedChange(event.currentTarget.checked)}
              />
              <Trans>Nur betroffene</Trans>
            </label>
          </div>
        </div>
      </div>

      {visibleRows.length === 0 ? (
        <section className="card dash-worklist">
          <EmptyState title={emptyText} />
        </section>
      ) : (
        sections
          .filter((section) => section.groups.length > 0 || section.empty !== null)
          .map((section) => {
            const title = i18n._(section.title);
            const customers = section.groups.length;
            const { instances } = section;
            return (
            <section
              key={section.state}
              id={sectionId(section.state)}
              className={`card dash-worklist dash-section${section.groups.length === 0 ? ' is-empty' : ''}`}
              aria-labelledby={`${sectionId(section.state)}-title`}
            >
              <div className="card-head table-head">
                <h2 id={`${sectionId(section.state)}-title`}>{title}</h2>
                <span className="table-count">
                  {customers === 0
                    ? section.empty && i18n._(section.empty)
                    : t`${plural(customers, { one: '# Kunde', other: '# Kunden' })} · ${plural(instances, { one: '# Auslieferung', other: '# Auslieferungen' })}`}
                </span>
              </div>
              {section.groups.length > 0 ? (
                <GroupTable
                  section={section.state}
                  caption={title}
                  groups={section.groups}
                  store={store}
                  gates={gates}
                  canReadTriage={canReadTriage}
                  emptyText={emptyText}
                  paginationResetKey={JSON.stringify([onlyAffected, search, section.state])}
                />
              ) : null}
            </section>
            );
          })
      )}
    </>
  );
}

/** The view „CVE“ (DEV-133): KPI row, search and the CVEs per area. */
function CveDashboardView({ data, loading, error, store, onOpenDeadlines }: Readonly<{
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  store: ExploitabilityStore;
  onOpenDeadlines: () => void;
}>): ReactNode {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const kpiTiles = useMemo(
    (): KpiItem[] => (data?.cves ? dashboardTiles(i18n,
      kpiCounters(data.overview.summary, data.events.offen, data.cves.summary.open_mails, dueDeadlines(data)), {
        openDeadlines: onOpenDeadlines,
        openTriage: () => navigate('/triage'),
        notify: () => navigate('/benachrichtigungen'),
      }) : []),
    // `locale`: the counters are translated, re-build them on a language switch.
    [data, navigate, onOpenDeadlines, i18n, locale],
  );

  if (!data?.cves) return <LoadState loading={loading} error={error} />;
  return (
    <>
      <LoadError error={error} />
      <KpiRow counters={kpiTiles} label={t`Kennzahlen zur aktuellen Sicherheitslage`} />
      <div className="card dash-filter">
        <div className="table-toolbar">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder={t`CVE, Produkt, Version, Kunde, Seriennummer oder Komponente suchen…`}
            aria-label={t`Schwachstellenübersicht filtern`}
            aria-controls={cveSectionId('in_progress')}
          />
        </div>
      </div>
      <CveView entries={data.cves.cves} events={data.events.events} search={search} store={store}
        generatedAt={data.cves.generated_at} />
    </>
  );
}

export function DashboardPage(): ReactNode {
  const { t, i18n } = useLingui();
  const { has } = useAuth();
  const canReadTriage = has('triage.read');
  const [searchParams] = useSearchParams();

  const [chosenView, setChosenView] = useState<DashboardView>(() => initialView(searchParams));
  // Deadlines and CVEs need the triage permission; without it the customers are all there is.
  const view: DashboardView = chosenView !== 'kunde' && !canReadTriage ? 'kunde' : chosenView;
  const [onlyAffected, setOnlyAffected] = useState(true);
  // „Fällige Meldefristen“ opens the deadlines filtered to the due cases.
  const [timelineFocus, setTimelineFocus] = useState<TimelineFocus>(null);

  const setView = useCallback((next: DashboardView) => {
    setTimelineFocus(null);
    setChosenView(next);
    writeView(next);
  }, []);
  const openDeadlines = useCallback(() => {
    setView('fristen');
    setTimelineFocus('due');
  }, [setView]);

  // KEV/EPSS facts are fetched only for what is on screen, and only with the
  // permission the endpoint requires.
  const exploitability = useExploitability(canReadTriage);

  // The overview keeps loading for its sync banner, but does not poll behind the deadlines.
  const { data, loading, error, syncStatus } = useDashboardData({ onlyAffected, paused: view === 'fristen' });
  const overview = view === 'fristen' ? null : data;
  // Every view shows its „Stand“ under the title, so switching does not shift the page.
  const [deadlineStand, setDeadlineStand] = useState<string | null>(null);
  const generatedAt = overview ? fmtDate(overview.overview.generated_at) : null;
  const stand = generatedAt !== null ? t`Stand ${generatedAt}` : deadlineStand;

  let content: ReactNode;
  if (view === 'fristen') {
    content = (
      <DeadlineTimeline key={timelineFocus ?? 'all'} linkedCaseId={linkedCase(searchParams)} onStand={setDeadlineStand}
        initialFocus={timelineFocus} />
    );
  } else if (view === 'cve') {
    content = (
      <CveDashboardView data={data} loading={loading} error={error} store={exploitability}
        onOpenDeadlines={openDeadlines} />
    );
  } else {
    content = (
      <OverviewView
        data={data}
        loading={loading}
        error={error}
        onlyAffected={onlyAffected}
        onOnlyAffectedChange={setOnlyAffected}
        canReadTriage={canReadTriage}
        store={exploitability}
        onOpenDeadlines={openDeadlines}
      />
    );
  }

  return (
    <>
      <PageHeader
        title={i18n._(VIEWS.find((item) => item.view === view)?.title ?? msg`Dashboard`)}
        sub={stand ?? undefined}
        actions={
          <>
            {loading && overview ? <Spinner label={t`Daten werden geladen…`} /> : null}
            <ViewSwitch current={view} withTriage={canReadTriage} onChange={setView} />
          </>
        }
      />

      {syncStatus !== null ? (
        <Banner kind={syncStatus === 'retrying' ? 'warn' : 'info'}>
          {syncStatus === 'retrying'
            ? <Trans>Die Übertragung an Dependency-Track ist fehlgeschlagen und wird automatisch wiederholt.</Trans>
            : <Trans>Änderungen werden an Dependency-Track übertragen. Ergebnisse folgen nach der Analyse.</Trans>}
        </Banner>
      ) : null}

      {content}
    </>
  );
}

export default DashboardPage;
